import React, { useMemo, useCallback, useState, useEffect } from 'react';
import useApiClient from "../../hooks/useApiClient";
import { useInfiniteQuery } from '@tanstack/react-query';
import { getIconForSealogEventValue } from '../timeline/util';
import { calcDateDuration, formatUtcDate, titleCase } from '../../utils';
import {
    RealAssetDeploymentWithChildrenResponse,
    RealAssetType,
    SQLModelPage_SealogEventGeographyResponse_,
    SealogEventGeographyResponse
} from '../../apiClient';
import { SealogEventValueDropdown } from './sealogEventValueDropdown';
import FilterChip from '../filterChip';
import FilterChipGroup from '../filterChip/filterChipGroup';
import { useDebouncedFunction } from "../../hooks/useDebouncedQueryParams";
import { DEBOUNCE_DELAY } from "../../utils";
import RealAssetCameraDropdown from '../realAsset/realAssetCameraDropdown';
import { EventDetails, TimelineItem } from "../timeline/types";
import VirtualizedTimeline from '../timeline/virtualizedTimeline';
import { cancelRequestOnSignal } from '@/apiUtils/cancelableQuery';

interface SealogEventTimelineProps {
    title?: string;
    realAssetDeploymentPublicId: string;
    onDataLoaded?: (total: number) => void;
    selectedEventId?: string | null;
    onEventSelect?: (
        publicId: string,
        lon?: number,
        lat?: number,
        eventDetails?: EventDetails
    ) => void;
    onEventHover?: (eventId: string | null) => void;
    deployment?: RealAssetDeploymentWithChildrenResponse;
    onAllTimelineItemsUpdated?: (dict: Record<string, TimelineItem>) => void;
    onAllFilteredEventIdsChanged?: (publicIds: Set<string>, filtersApplied: boolean) => void;
    cardShouldFlash?: boolean;
    onEndFlash?: () => void;
}

interface SealogEventWithIndex extends SealogEventGeographyResponse {
    originalIndex: number;
}

const useSealogEvents = (realAssetDeploymentPublicId: string) => {
    const api = useApiClient();

    const fetchSealogEvents = useCallback(({ pageParam, signal }: { pageParam: number; signal: AbortSignal }): Promise<SQLModelPage_SealogEventGeographyResponse_> => {
        return cancelRequestOnSignal(
            api.sealog.searchSealogEvent({
                realAssetDeploymentPublicId,
                includeDataAssets: true,
                page: pageParam,
                size: 200,
            }),
            signal,
        );
    }, [realAssetDeploymentPublicId, api.sealog]);

    return useInfiniteQuery({
        queryKey: ["searchSealogEvent", realAssetDeploymentPublicId],
        queryFn: fetchSealogEvents,
        initialPageParam: 1,
        getNextPageParam: (lastPage: SQLModelPage_SealogEventGeographyResponse_) => {
            const nextPage = lastPage.page + 1;
            const totalPages = Math.ceil(lastPage.total / lastPage.size);
            return nextPage <= totalPages ? nextPage : undefined;
        },
        refetchOnWindowFocus: false,
        staleTime: 60_000,
    });
};

const extractCamerasFromDeployment = (deployment: RealAssetDeploymentWithChildrenResponse): string[] => {
    const cameras = new Set<string>();

    if (deployment.real_asset.type === RealAssetType.CAMERA) {
        cameras.add(deployment.real_asset.name);
    }

    if (deployment.child_deployments) {
        deployment.child_deployments.forEach(child => {
            if (child.real_asset.type === RealAssetType.CAMERA) {
                cameras.add(child.real_asset.name);
            }
        });
    }

    return Array.from(cameras);
};

function filterSealogEvents(
    events: SealogEventWithIndex[],
    searchTerm: string,
    selectedEventTypes: string[]
): SealogEventWithIndex[] {
    const tokens = searchTerm
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);

    return events.filter((eventObj) => {
        const valLower = eventObj.event.event_value.toLowerCase();
        const freeLower = (eventObj.event.event_free_text || "").toLowerCase();

        const matchesAllSearch = tokens.every((t) =>
            valLower.includes(t) ||
            freeLower.includes(t) ||
            (eventObj.event.event_options || []).some((opt: any) =>
                opt.event_option_name.toLowerCase().includes(t) ||
                opt.event_option_value.toLowerCase().includes(t)
            )
        );

        const matchesEventType =
            selectedEventTypes.length === 0 || selectedEventTypes.includes(valLower);

        return matchesAllSearch && matchesEventType;
    });
}

const SealogEventTimeline: React.FC<SealogEventTimelineProps> = ({
    title,
    realAssetDeploymentPublicId,
    onDataLoaded,
    selectedEventId = null,
    onEventSelect,
    onEventHover,
    deployment,
    onAllTimelineItemsUpdated,
    onAllFilteredEventIdsChanged,
    cardShouldFlash,
    onEndFlash,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, _setDebounceSearchTerm] = useState('');
    const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<string | null>(null);

    const cameras = useMemo(() =>
        deployment ? extractCamerasFromDeployment(deployment) : [],
        [deployment]
    );

    const setDebounceSearchTerm = useDebouncedFunction(
        _setDebounceSearchTerm,
        DEBOUNCE_DELAY
    );

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setSearchTerm(value);
            setDebounceSearchTerm(value);
        },
        [setDebounceSearchTerm]
    );

    const filtersApplied = Boolean(
        debouncedSearchTerm.trim() || selectedEventTypes.length > 0
    );

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        status,
        isFetching
    } = useSealogEvents(realAssetDeploymentPublicId);

    useEffect(() => {
        if (data?.pages[0]) {
            onDataLoaded?.(data.pages[0].total);
        }
    }, [data?.pages, onDataLoaded]);

    const allEvents = useMemo<SealogEventWithIndex[]>(() => {
        return (data?.pages.flatMap(page => page.items) ?? []).map((eventObj, idx) => ({
            ...eventObj,
            originalIndex: idx,
        }));
    }, [data]);


    useEffect(() => {
        if (filtersApplied && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [filtersApplied, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const filteredEvents = useMemo(() => {
        if (!filtersApplied) {
            return allEvents;
        }
        return filterSealogEvents(
            allEvents,
            debouncedSearchTerm,
            selectedEventTypes
        );
    }, [filtersApplied, allEvents, debouncedSearchTerm, selectedEventTypes]);

    useEffect(() => {
        if (!filtersApplied) {
            onAllFilteredEventIdsChanged?.(new Set(), false);
        } else {
            onAllFilteredEventIdsChanged?.(
                new Set(filteredEvents.map((e) => e.public_id).filter((id): id is string => id != null)),
                true
            );
        }
    }, [filtersApplied, filteredEvents, onAllFilteredEventIdsChanged]);

    const timelineItems = useMemo(() => {
        return filteredEvents.map((eventObj) => {
            const start = deployment?.deployment_start_dt;
            const ts = eventObj.event.ts;
            const timeFromStart =
                start && ts ? calcDateDuration(start, ts, true) : "N/A";

            return {
                title: eventObj.event.event_value,
                publicId: eventObj.public_id,
                description: eventObj.event.event_free_text ?? "",
                date: formatUtcDate(eventObj.event.ts),
                icon: getIconForSealogEventValue(
                    eventObj.event.event_value,
                    "text-blue-500"
                ),
                rawEvent: eventObj.event,
                data_assets: eventObj.data_assets,
                index: eventObj.originalIndex,
                lat: eventObj.lat,
                lng: eventObj.lng,
                depth: eventObj.depth,
                timeFromStart,
            } as TimelineItem;
        });
    }, [filteredEvents, deployment]);

    const timelineItemsWithConsecutiveFlag = useMemo(() => {
        return timelineItems.map((item, idx, array) => ({
            ...item,
            isConsecutive:
                idx === array.length - 1 || item.index === array[idx + 1]?.index + 1,
        }));
    }, [timelineItems]);

    const timelineItemsById = useMemo(() => {
        const dict: Record<string, TimelineItem> = {};
        for (const item of timelineItemsWithConsecutiveFlag) {
            dict[item.publicId] = item;
        }
        return dict;
    }, [timelineItemsWithConsecutiveFlag]);

    useEffect(() => {
        onAllTimelineItemsUpdated?.(timelineItemsById);
    }, [timelineItemsById, onAllTimelineItemsUpdated]);

    const handleEventTypeToggle = useCallback((option: string) => {
        setSelectedEventTypes((prev) =>
            prev.includes(option) ? prev.filter((type) => type !== option) : [...prev, option]
        );
    }, []);

    if (status === "pending") {
        return <div aria-live="polite" aria-busy="true">Loading Sealog events...</div>;
    }

    return (
        <div aria-label="Sealog Event Timeline" role="region">
            <div className="flex space-x-3 mb-2 border-b-[1px] border-gray-300 pb-2">
                <div>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search events..."
                        className="w-[10rem] px-4 py-[6px] border border-gray-300 rounded-md focus:outline-none"
                    />
                </div>

                <div className="z-50">
                    <SealogEventValueDropdown
                        dropdownProps={{
                            selections: selectedEventTypes,
                            onOptionToggle: handleEventTypeToggle,
                            className: "w-[9rem]",
                        }}
                        apiOptions={{
                            realAssetDeploymentPublicId: realAssetDeploymentPublicId,
                        }}
                    />
                </div>

                {cameras.length > 0 &&
                    <div className="w-64 z-50">
                        <RealAssetCameraDropdown
                            selection={selectedCamera}
                            cameras={cameras}
                            onSelect={setSelectedCamera}
                            className="w-[184px] 3xl:w-48"
                        />
                    </div>
                }
            </div>

            {selectedEventTypes.length > 0 && (
                <div>
                    <FilterChipGroup
                        title="Event Types"
                        items={selectedEventTypes}
                        maxVisible={3}
                        renderChip={(value) => (
                            <FilterChip
                                key={value}
                                title="Event Type"
                                label={titleCase(value)}
                                onClose={() => handleEventTypeToggle(value)}
                            />
                        )}
                    />
                </div>
            )}

            {timelineItemsWithConsecutiveFlag.length === 0 && isFetching ? (
                <div>Loading Sealog events...</div>
            ) : timelineItemsWithConsecutiveFlag.length === 0 ? (
                <div>No matching Sealog events found.</div>
            ) : (
                <VirtualizedTimeline
                    items={timelineItemsWithConsecutiveFlag}
                    title={title ?? ""}
                    isLoading={isFetchingNextPage}
                    selectedEventId={selectedEventId}
                    onEventSelect={onEventSelect}
                    onEventHover={onEventHover}
                    cardShouldFlash={cardShouldFlash}
                    onEndFlash={onEndFlash}
                    selectedCamera={selectedCamera}
                />
            )}
        </div>
    );
};

export default SealogEventTimeline;
